import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
  Button,
  Box,
  Tag,
  TagLabel,
  TagCloseButton,
  Select,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  useToast,
  Spinner,
  List,
  ListItem,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import axios from "axios";

const brands = ["KR", "SBC", "JB", "PB", "RJ", "LAC", "CN", "KT", "GL"];

const UpdateAccessModal = ({ modalOpen, modalClose, user, onSuccess }) => {
  const toast = useToast();
  const [access, setAcces] = useState([]);
  const [availableBrands, setAvailable] = useState([...brands]);
  const [trigger, setTrigger] = useState(false); // this state will be triggered so that the display will rerender and display the updated layout.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [storeSearch, setStoreSearch] = useState("");
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const storeSearchRef = useRef(null);

  useEffect(() => {
    if (user.selectedUserAcces) {
      setAcces(user.selectedUserAcces);
    }
    if (user.selectedUserStore) {
      // Handle both array and string formats for backward compatibility
      const storeArray = Array.isArray(user.selectedUserStore)
        ? user.selectedUserStore
        : user.selectedUserStore
          ? [user.selectedUserStore]
          : [];
      setSelectedStores(storeArray);
    }
  }, [user.selectedUserAcces, user.selectedUserStore]);

  useEffect(() => {
    const fetchStores = async () => {
      setLoading(true);
      try {
        const resStores = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/maintenance/getAllStore`,
        );
        setStores(resStores.data);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        toast({
          title: "Error",
          position: "top-right",
          description: "Failed to fetch stores.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    };
    if (modalOpen) {
      fetchStores();
    }
  }, [modalOpen, toast]);
  const removeBrand = (brandToRemove) => {
    const updatedAcces = access.filter((brand) => brand !== brandToRemove);
    setAcces(updatedAcces);
    setTrigger((prev) => !prev);
  };
  const addBrand = (brandToAdd) => {
    const updatedAcces = [...access, brandToAdd];
    setAcces(updatedAcces);
  };

  const addStore = (storeToAdd) => {
    const updatedStores = [...selectedStores, storeToAdd];
    setSelectedStores(updatedStores);
  };

  const removeStore = (storeToRemove) => {
    const updatedStores = selectedStores.filter(
      (store) => store !== storeToRemove,
    );
    setSelectedStores(updatedStores);
  };
  const submitHandler = async () => {
    setIsSubmitting(true);
    if (access.length <= 0) {
      toast({
        title: "Error",
        position: "top-right",
        description: "User Access must not be empty.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      setIsSubmitting(false);
      return;
    }
    const originalStores = user.selectedUserStore
      ? Array.isArray(user.selectedUserStore)
        ? user.selectedUserStore
        : [user.selectedUserStore]
      : [];

    const storesChanged =
      JSON.stringify(originalStores.sort()) !==
      JSON.stringify(selectedStores.sort());

    if (
      JSON.stringify(user.selectedUserAcces) === JSON.stringify(access) &&
      !storesChanged
    ) {
      toast({
        title: "Error",
        position: "top-right",
        description:
          "You made no changes in this User Access or Store assignment.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      setIsSubmitting(false);
      return;
    }
    try {
      console.log(process.env.REACT_APP_BACKEND_API_URL);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/updateUserAccess`,
        {
          id: user.selectedUserId,
          access: access,
          storeName: selectedStores,
        },
        { withCredentials: true },
      );
      toast({
        title: "Success",
        position: "top-right",
        description:
          "You successfully updated the user Access and Store assignment.",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
      setIsSubmitting(false);
      modalClose();
      onSuccess();
    } catch (err) {
      toast({
        title: "Error",
        position: "top-right",
        description: err.response.data.message,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
    setIsSubmitting(false);
  };
  return (
    <Modal blockScrollOnMount={false} isOpen={modalOpen} onClose={modalClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Update Access</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box>
            <Text as="b" mb="1rem" mr={3}>
              ID:
            </Text>
            {user.selectedUserId}
          </Box>
          <Box>
            <Text as="b" mb="1rem" mr={3}>
              User:
            </Text>
            {user.selectedUser}
          </Box>
          <Box>
            <Text as="b" mb="1rem" mr={3}>
              Assigned Stores:
            </Text>
            {user.selectedUserStore && user.selectedUserStore.length > 0
              ? Array.isArray(user.selectedUserStore)
                ? user.selectedUserStore.join(", ")
                : user.selectedUserStore
              : "N/A"}
          </Box>
          <FormControl mb={4}>
            <FormLabel fontWeight="bold">Update Assigned Stores:</FormLabel>
            <Box mb={2}>
              {selectedStores.length === 0
                ? "No stores selected."
                : selectedStores.map((store, index) => (
                    <Tag
                      size="lg"
                      key={index}
                      borderRadius="full"
                      variant="solid"
                      colorScheme="blue"
                      ml={2}
                      mb={2}
                      onClick={() => removeStore(store)}
                    >
                      <TagLabel>{store}</TagLabel>
                      <TagCloseButton />
                    </Tag>
                  ))}
            </Box>
            <Box position="relative" ref={storeSearchRef}>
              <InputGroup>
                <Input
                  placeholder={loading ? "Loading stores..." : "Type to search store..."}
                  value={storeSearch}
                  isDisabled={loading}
                  onChange={(e) => {
                    setStoreSearch(e.target.value);
                    setShowStoreDropdown(true);
                  }}
                  onFocus={() => setShowStoreDropdown(true)}
                  autoComplete="off"
                />
                {storeSearch && (
                  <InputRightElement>
                    <IconButton
                      size="xs"
                      icon={<CloseIcon />}
                      aria-label="Clear"
                      variant="ghost"
                      onClick={() => {
                        setStoreSearch("");
                        setShowStoreDropdown(false);
                      }}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              {showStoreDropdown && storeSearch && (
                <Box
                  position="absolute"
                  zIndex={10}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  boxShadow="md"
                  w="100%"
                  maxH="200px"
                  overflowY="auto"
                >
                  <List>
                    {stores
                      .filter(
                        (store) =>
                          !selectedStores.includes(store.StoreName) &&
                          store.StoreName?.toLowerCase().includes(storeSearch.toLowerCase())
                      )
                      .map((store) => (
                        <ListItem
                          key={store.StoreID}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: "blue.50" }}
                          onMouseDown={() => {
                            addStore(store.StoreName);
                            setStoreSearch("");
                            setShowStoreDropdown(false);
                          }}
                        >
                          {store.StoreName}
                        </ListItem>
                      ))}
                    {stores.filter(
                      (store) =>
                        !selectedStores.includes(store.StoreName) &&
                        store.StoreName?.toLowerCase().includes(storeSearch.toLowerCase())
                    ).length === 0 && (
                      <ListItem px={3} py={2} color="gray.400">
                        No stores found.
                      </ListItem>
                    )}
                  </List>
                </Box>
              )}
            </Box>
          </FormControl>
          <Box mt={5} mb={4}>
            <Text as="b" mr={3}>
              This User Access:
            </Text>
          </Box>
          <Box mb={4}>
            {access.length === 0
              ? "This user has no access."
              : access.map((brand) => {
                  let colorScheme = "gray";
                  switch (brand) {
                    case "KR":
                      colorScheme = "orange";
                      break;
                    case "PB":
                      colorScheme = "blue";
                      break;
                    case "JB":
                      colorScheme = "green";
                      break;
                    case "SBC":
                      colorScheme = "red";
                      break;
                    case "RJ":
                      colorScheme = "gray";
                      break;
                    case "LAC":
                      colorScheme = "red";
                      break;
                    case "KT":
                      colorScheme = "red";
                      break;
                    case "GL":
                      colorScheme = "orange";
                      break;
                    default:
                      colorScheme = "gray";
                  }
                  return (
                    <Tag
                      size={"lg"}
                      key={brand}
                      borderRadius="full"
                      variant="solid"
                      colorScheme={colorScheme}
                      ml={2}
                      mb={2}
                      onClick={() => removeBrand(brand)}
                    >
                      <TagLabel>{brand}</TagLabel>
                      <TagCloseButton />
                    </Tag>
                  );
                })}
          </Box>
          {access.length <= 99 && (
            <Box>
              <Text as={"b"}>Add Brand:</Text>
              <Box mt={4}>
                {availableBrands
                  .filter((brand) => !access.includes(brand)) // Filter out brands already in user's access
                  .map((brand) => {
                    let colorScheme = "gray";
                    switch (brand) {
                      case "KR":
                        colorScheme = "orange";
                        break;
                      case "PB":
                        colorScheme = "blue";
                        break;
                      case "JB":
                        colorScheme = "green";
                        break;
                      case "SBC":
                        colorScheme = "red";
                        break;
                      case "RJ":
                        colorScheme = "gray";
                        break;
                      case "LAC":
                        colorScheme = "red";
                        break;
                      case "KT":
                        colorScheme = "red";
                        break;
                      default:
                        colorScheme = "gray";
                    }
                    return (
                      <Tag
                        size={"lg"}
                        key={brand}
                        borderRadius="full"
                        variant="solid"
                        colorScheme={colorScheme}
                        ml={2}
                        onClick={() => addBrand(brand)}
                        cursor={"pointer"}
                        mb={2}
                      >
                        <TagLabel>{brand}</TagLabel>
                      </Tag>
                    );
                  })}
              </Box>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="blue"
            onClick={submitHandler}
            isDisabled={isSubmitting}
          >
            {isSubmitting ? <Spinner /> : "Submit"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UpdateAccessModal;
